import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useSmartLoading } from '../../hooks/useSmartLoading';
import FormDataCard from '../../components/cards/FormDataCard';
import { ICONS } from '../../constants/imageConstants';
import FormListPageSkeleton from '../../components/skeletons/FormListPageSkeleton';
import { parseAddress } from '../../utils/location';
import { useSync } from '../../hooks/useSync';
import { useFormData } from '../../hooks/useFormData';
import DataListContainer from '../../components/layout/DataListContainer';
import PageLayout from '../../components/layout/PageLayout';
import { PlusIcon } from '../../components/Icons/svgIcons';
import { cn } from '../../lib/utils';

const OfflineStateIcon = ({ className = "w-14 h-14" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22.61 16.95A5 5 0 0 0 18 10h-1.26a8 8 0 0 0-7.05-6M5 5a8 8 0 0 0 4 15h9a5 5 0 0 0 1.7-.3" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const WarningStateIcon = ({ className = "w-14 h-14" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const FormDataList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const formId = searchParams.get('formId');
  const filter = location.state?.filter;
  const fromSubmission = location.state?.fromSubmission;
  const showUnsyncedOnly = location.state?.showUnsyncedOnly;
  const { isOnline } = useSync();
  const [isInitializing, setIsInitializing] = useState(true);
  const [isCheckingPermissions, setIsCheckingPermissions] = useState(true);
  const [showSubmissionSkeleton, setShowSubmissionSkeleton] = useState(fromSubmission);

  // Removed unused forms and stats hooks
  const { status: fetchStatus, error } = useSelector(state => state.submissions);

  const {
    formTitle,
    submissions: allSubmissions,
    status,
    handleDelete
  } = useFormData(filter);

  const submissions = useMemo(() => {
    if (showUnsyncedOnly) {
      return allSubmissions.filter(sub => sub.syncStatus === 'pending');
    }
    return allSubmissions;
  }, [allSubmissions, showUnsyncedOnly]);

  useEffect(() => {
    const timer = setTimeout(() => setIsInitializing(false), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (status !== 'loading') {
      const timer = setTimeout(() => setIsCheckingPermissions(false), 300);
      return () => clearTimeout(timer);
    }
  }, [status]);

  useEffect(() => {
    if (fromSubmission) {
      const timer = setTimeout(() => setShowSubmissionSkeleton(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [fromSubmission]);

  const { showSkeleton } = useSmartLoading([status], submissions.length > 0);
  const handleView = useCallback(id => navigate(`/forms/data/${id}`), [navigate]);

  const handleEdit = useCallback(item => {
    const fId = item.form?.id || item.form_id || item.formId || formId;
    if (fId && item.id) {
      navigate(`/forms/edit/${fId}/${item.id}`);
    }
  }, [navigate, formId]);


  const handleAddNewData = e => {
    e.stopPropagation();
    if (formId) {
      navigate(`/forms/fill/${formId}`);
    }
  };

  const isUnauthorized = useMemo(() => {
    // Only consider unauthorized if we specifically have a 403/401 error message from the API
    // or if the submissions slice specifically flagged it.
    if (fetchStatus !== 'failed' || !error) return false;
    
    return (
      error.toLowerCase().includes('403') || 
      error.toLowerCase().includes('401') ||
      error.toLowerCase().includes('unauthorized') ||
      error.toLowerCase().includes('permission')
    );
  }, [fetchStatus, error]);

  useEffect(() => {
    if (!isInitializing && status !== 'loading' && isUnauthorized && location.state?.from !== '/forms/completed' && !fromSubmission) {
      if (isOnline) {
        navigate('/dashboard', { 
          replace: true,
          state: { unauthorizedAccess: true } 
        });
      }
    }
  }, [isUnauthorized, isInitializing, status, navigate, location.state, fromSubmission, isOnline]);

  const emptyStateProps = useMemo(() => {
    if (isUnauthorized) {
      if (!isOnline) {
        return {
          title: "Offline",
          message: "Synced submissions can be viewed in online mode.",
          icon: <OfflineStateIcon className="w-14 h-14 icon-theme" />
        };
      }
      return {
        title: "Access Restricted",
        message: error || "You do not have permission to view these submissions.",
        icon: <WarningStateIcon className="w-14 h-14 icon-theme" />
      };
    }
    return {
      title: filter === 'pending' ? "No Pending Submissions" : "No Submissions Yet",
      message: filter === 'pending' ? "All submissions for this form are synced." : "Submissions will appear here once added."
    };
  }, [isUnauthorized, filter, isOnline, error]);

  const isLoading = isInitializing || (status === 'loading' && submissions.length === 0) || (showSkeleton && submissions.length === 0) || (isOnline && isCheckingPermissions) || showSubmissionSkeleton;

  return (
    <div className="h-full w-full bg-[var(--bg-body)] flex flex-col overflow-hidden relative">
      <PageLayout 
        title={formTitle} 
        showBackButton 
        rightContent={
          <span className="bg-blue-50 text-[var(--header-bg)] text-xs font-bold px-2.5 py-1 rounded">
            {submissions.length}
          </span>
        }
      >
        {/* Extra bottom padding so the FAB doesn't cover last card */}
        <div className="pb-20">
          <DataListContainer
            isLoading={isLoading}
            data={isUnauthorized ? [] : submissions}
            skeletonComponent={<FormListPageSkeleton />}
            emptyStateProps={emptyStateProps}
          >
            <div className="space-y-3">
              {submissions.map((item) => (
                <FormDataCard
                  key={item.id}
                  name={item.form?.name || item.formName || formTitle}
                  location={parseAddress(item.address)}
                  syncStatus={item.syncStatus || 'synced'}
                  createdAt={item.created_at || item.createdAt}
                  coordinates={{ latitude: item.latitude, longitude: item.longitude }}
                  onView={() => handleView(item.id)}
                  onEdit={() => handleEdit(item)}
                  onDelete={() => handleDelete(item.id)}
                />
              ))}
            </div>
          </DataListContainer>
        </div>
      </PageLayout>

      {/* FAB — absolute inside the mobile frame, above the nav bar */}
      {formId && (
        <button
          onClick={handleAddNewData}
          className={cn(
            "absolute bottom-20 right-4 z-50 w-14 h-14 rounded-full flex justify-center items-center shadow-lg transition-all duration-200",
            "bg-[var(--btn-secondary-bg)] hover:bg-[var(--btn-secondary-hover)] active:scale-95 hover:shadow-xl border-2 border-white"
          )}
          aria-label="Add new submission"
        >
          <PlusIcon className="w-7 h-7 text-white" />
        </button>
      )}
    </div>
  );
};

export default FormDataList;